import { CloseRounded } from "@mui/icons-material";
import { Box, Button, Chip, Dialog, Divider, IconButton, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";
import { BANNER_TYPE_OPTIONS, BannerType } from "../../models/cms";
import { StatusFilter } from "../banner_listing/banner_listing";

export interface BannersFilterValues {
  statusFilter: StatusFilter;
  bannerType: BannerType | null;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

const popoverPaperSx = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
} as const;

const STATUS_OPTIONS: { value: Exclude<StatusFilter, "all">; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

interface FilterBannersProps extends BannersFilterValues {
  open: boolean;
  onClose: () => void;
  onApply: (values: BannersFilterValues) => void;
}

export default function FilterBanners({
  open,
  onClose,
  statusFilter,
  bannerType,
  dateFrom,
  dateTo,
  onApply,
}: FilterBannersProps) {
  const [_status, _setStatus] = useState<StatusFilter>(statusFilter);
  const [_bannerType, _setBannerType] = useState<BannerType | null>(bannerType);
  const [_dateFrom, _setDateFrom] = useState<Dayjs | null>(dateFrom);
  const [_dateTo, _setDateTo] = useState<Dayjs | null>(dateTo);

  const handleClose = () => onClose();

  const handleReset = () => {
    onApply({ statusFilter: "all", bannerType: null, dateFrom: null, dateTo: null });
    onClose();
  };

  const handleApply = () => {
    onApply({ statusFilter: _status, bannerType: _bannerType, dateFrom: _dateFrom, dateTo: _dateTo });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 400, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Banners
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Status
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {STATUS_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                variant={_status === option.value ? "filled" : "outlined"}
                color="primary"
                onClick={() =>
                  _setStatus((prev) => (prev === option.value ? "all" : option.value))
                }
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Banner Type
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {BANNER_TYPE_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                variant={_bannerType === option.value ? "filled" : "outlined"}
                color="primary"
                onClick={() =>
                  _setBannerType((prev) => (prev === option.value ? null : option.value))
                }
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={1}>
            Created Date
          </Typography>
          <Box display="flex" gap={1.5}>
            <DatePicker
              label="From Date"
              value={_dateFrom}
              maxDate={_dateTo || undefined}
              onChange={(value: Dayjs | null) => _setDateFrom(value)}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              value={_dateTo}
              minDate={_dateFrom || undefined}
              onChange={(value: Dayjs | null) => _setDateTo(value)}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
